import React, { useEffect, useState, useCallback } from 'react';
import { BadgeCheck, LogOut, UserX } from 'lucide-react';
import { get, post } from '../api/client';
import { useAuth } from '../hooks/useAuth';

const EMPTY = {
  member_id: '',
  event_label: '',
  guardian_name: '',
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500';

function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return d.toLocaleString();
}

export default function Checkin() {
  const { isViewer } = useAuth();
  const [active, setActive] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await get('/api/v1/checkin/active');
      setActive(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce, matches Members.jsx pattern
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    get('/api/v1/members', { params: { limit: 500 } })
      .then(setMembers)
      .catch(() => {});
  }, []);

  const memberName = (id) => {
    const m = members.find((x) => x.id === id);
    return m ? `${m.first_name} ${m.last_name}` : '—';
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFormError('');
    try {
      await post('/api/v1/checkin', {
        member_id: form.member_id,
        event_label: form.event_label,
        ...(form.guardian_name ? { guardian_name: form.guardian_name } : {}),
      });
      setForm(EMPTY);
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const checkout = async (rec) => {
    if (!window.confirm(`Check out ${memberName(rec.member_id)}?`)) return;
    try {
      await post(`/api/v1/checkin/${rec.id}/checkout`);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
          Check-in
        </h2>
        {isViewer && (
          <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 rounded-full px-2 py-0.5">
            Read-only demo
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        {!isViewer && (
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
                <BadgeCheck size={18} className="text-brand-600" /> New check-in
              </h3>
              <form onSubmit={submit} className="space-y-3">
                {formError && (
                  <div className="bg-red-50 text-red-700 text-sm rounded-lg px-3 py-2">
                    {formError}
                  </div>
                )}
                <Field label="Member">
                  <select
                    required
                    className={inputCls}
                    value={form.member_id}
                    onChange={(e) => setForm({ ...form, member_id: e.target.value })}
                  >
                    <option value="">— Select member —</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.first_name} {m.last_name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Event">
                  <input
                    required
                    className={inputCls}
                    placeholder="e.g. Sunday Kids"
                    value={form.event_label}
                    onChange={(e) => setForm({ ...form, event_label: e.target.value })}
                  />
                </Field>
                <Field label="Guardian name">
                  <input
                    className={inputCls}
                    value={form.guardian_name}
                    onChange={(e) => setForm({ ...form, guardian_name: e.target.value })}
                  />
                </Field>
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg px-4 py-2 flex items-center justify-center gap-1"
                >
                  <BadgeCheck size={16} /> {saving ? 'Checking in…' : 'Check in'}
                </button>
              </form>
            </div>
          </div>
        )}

        <div className={isViewer ? 'lg:col-span-3' : 'lg:col-span-2'}>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700">
              <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                Currently checked in
              </h3>
            </div>
            {loading ? (
              <div className="p-8 text-center text-slate-400">Loading…</div>
            ) : active.length === 0 ? (
              <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-2">
                <UserX size={28} />
                No one is checked in.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-300 text-left">
                  <tr>
                    <th className="px-4 py-2 font-medium">Member</th>
                    <th className="px-4 py-2 font-medium">Event</th>
                    <th className="px-4 py-2 font-medium">Guardian</th>
                    <th className="px-4 py-2 font-medium">Code</th>
                    <th className="px-4 py-2 font-medium">Time in</th>
                    <th className="px-4 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {active.map((rec) => (
                    <tr key={rec.id} className="border-t border-slate-100 dark:border-slate-700">
                      <td className="px-4 py-2 font-medium text-slate-800 dark:text-slate-100">
                        {memberName(rec.member_id)}
                      </td>
                      <td className="px-4 py-2 text-slate-500 dark:text-slate-400">
                        {rec.event_label}
                      </td>
                      <td className="px-4 py-2 text-slate-500 dark:text-slate-400">
                        {rec.guardian_name || '—'}
                      </td>
                      <td className="px-4 py-2">
                        <span className="inline-block rounded-full px-2 py-0.5 text-xs font-mono font-semibold bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                          {rec.security_code}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-slate-500 dark:text-slate-400">
                        {fmtTime(rec.checked_in_at)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex justify-end gap-2 text-slate-400">
                          {isViewer ? (
                            <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                          ) : (
                            <button
                              onClick={() => checkout(rec)}
                              className="text-xs font-medium text-red-600 hover:text-red-700 flex items-center gap-1"
                              title="Check out"
                            >
                              <LogOut size={14} /> Check out
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
